// src/components/settings/shared/HeaderSettings.jsx
import React, { useState } from 'react';
import { 
  Eye, 
  EyeOff, 
  User, 
  Search, 
  Filter, 
  Share2, 
  ArrowLeft, 
  Grid, 
  List, 
  Globe,
  Lock,
  Users,
  BookOpen,
  ShoppingCart,
  Heart,
  MessageCircle,
  Upload,
  Settings,
  Layout,
  Palette,
  Check
} from 'lucide-react';

export const HeaderSettings = ({ 
  formData, 
  onInputChange, 
  uploadStates, 
  onImageUpload, 
  errors,
  templateType = 'album' 
}) => {
  const [headerPreview, setHeaderPreview] = useState(true);

  const handleToggleChange = (key, value) => {
    onInputChange(`headerSettings.${key}`, value);
  };

  const handleHeightChange = (key, value) => {
    const numValue = parseInt(value) || 60;
    const clampedValue = Math.max(40, Math.min(120, numValue));
    onInputChange(`headerSettings.${key}`, clampedValue);
  };

  // Helper to get the actual checked value
  const getCheckedValue = (key, defaultValue) => {
    if (formData.headerSettings?.[key] !== undefined) {
      return formData.headerSettings[key];
    }
    return defaultValue;
  };

  const ecommerceElements = templateType === 'ecommerce' ? [
    {
      key: 'showCart',
      label: 'Show Shopping Cart',
      description: 'Display shopping cart button with item count badge',
      icon: ShoppingCart,
      defaultValue: true
    },
    {
      key: 'showWishlist',
      label: 'Show Wishlist',
      description: 'Display wishlist/favorites button',
      icon: Heart,
      defaultValue: true
    },
    {
      key: 'showContact',
      label: 'Show Contact',
      description: 'Display contact button for customer inquiries',
      icon: MessageCircle,
      defaultValue: true
    },
    {
      key: 'showCategoryNav',
      label: 'Show Category Navigation',
      description: 'Display category menu in header',
      icon: Grid,
      defaultValue: true
    }
  ] : [];

  // Define header elements configuration
  const headerElements = [
    {
      key: 'showLogo',
      label: 'Show Logo',
      description: 'Display album logo in header',
      icon: Layout,
      defaultValue: true
    },
    {
      key: 'showTitle',
      label: 'Show Title',
      description: 'Display album title next to logo',
      icon: Layout,
      defaultValue: true
    },
    {
      key: 'showDescription',
      label: 'Show Description',
      description: 'Display album description under title',
      icon: BookOpen,
      defaultValue: false
    },
    {
      key: 'showSearch',
      label: 'Search Bar',
      description: 'Enable media search functionality',
      icon: Search,
      defaultValue: true
    },
    {
      key: 'showShareButton',
      label: 'Share Button',
      description: 'Allow users to share the album',
      icon: Share2,
      defaultValue: true
    },
    {
      key: 'showAccessLevel',
      label: 'Access Level Badge',
      description: 'Show public/private indicator',
      icon: Globe,
      defaultValue: true
    },
    {
      key: 'showFilterControls',
      label: 'Filter Controls',
      description: 'Show/hide filter and view options',
      icon: Filter,
      defaultValue: true
    },
    {
      key: 'showGallerySelector',
      label: 'Gallery Selector',
      description: 'Dropdown to switch between galleries',
      icon: Grid,
      defaultValue: true
    },
    {
      key: 'showViewModeToggle',
      label: 'View Mode Toggle',
      description: 'Masonry/Grid/List view buttons',
      icon: List,
      defaultValue: true
    },
    {
      key: 'showOwnerControls',
      label: 'Owner Controls',
      description: 'Dashboard return & story creation buttons',
      icon: User,
      defaultValue: true
    }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Header Configuration</h3>
        <p className="text-sm text-gray-600">
          Configure the header appearance and functionality for your album.
        </p>
      </div>

      {/* Header Elements Visibility */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-gray-900 flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Header Elements
          </h4>
          <div className="text-xs text-gray-500">
            {headerElements.filter(({ key, defaultValue }) => 
              getCheckedValue(key, defaultValue)
            ).length} of {headerElements.length} visible
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {headerElements.map(({ key, label, description, icon: Icon, defaultValue }) => {
            const isChecked = getCheckedValue(key, defaultValue);
            const isDefault = formData.headerSettings?.[key] === undefined;
            
            return (
              <label 
                key={key} 
                className={`
                  flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all
                  ${isChecked 
                    ? 'border-blue-500 bg-blue-50 hover:bg-blue-100' 
                    : 'border-gray-200 bg-white hover:bg-gray-50'
                  }
                `}
              >
                <div className="relative flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => handleToggleChange(key, e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
                  />
                  {isChecked && (
                    <Check className="absolute inset-0 w-5 h-5 text-blue-600 pointer-events-none" />
                  )}
                </div>
                
                <Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${isChecked ? 'text-blue-600' : 'text-gray-400'}`} />
                
                <div className="flex-1 min-w-0">
                  <div className={`font-medium ${isChecked ? 'text-gray-900' : 'text-gray-600'}`}>
                    {label}
                    {isDefault && (
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        (default)
                      </span>
                    )}
                  </div>
                  <div className={`text-sm mt-0.5 ${isChecked ? 'text-gray-600' : 'text-gray-500'}`}>
                    {description}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="flex items-center gap-3 mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={() => {
              headerElements.forEach(({ key }) => handleToggleChange(key, true));
            }}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-2"
          >
            <Eye className="h-4 w-4" />
            Show All
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={() => {
              headerElements.forEach(({ key }) => handleToggleChange(key, false));
            }}
            className="text-sm text-gray-600 hover:text-gray-800 font-medium flex items-center gap-2"
          >
            <EyeOff className="h-4 w-4" />
            Hide All
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={() => {
              headerElements.forEach(({ key, defaultValue }) => {
                handleToggleChange(key, defaultValue);
              });
            }}
            className="text-sm text-gray-600 hover:text-gray-800 font-medium flex items-center gap-2"
          >
            <Settings className="h-4 w-4" />
            Reset to Defaults
          </button>
        </div>
      </div>

      {templateType === 'ecommerce' && ecommerceElements.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h5 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <ShoppingCart className="h-4 w-4 text-blue-600" />
            Ecommerce Elements
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ecommerceElements.map(({ key, label, description, icon: Icon, defaultValue }) => (
              <label key={key} className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={formData.headerSettings?.[key] ?? defaultValue}
                  onChange={(e) => handleToggleChange(key, e.target.checked)}
                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Icon className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900">{label}</div>
                  <div className="text-sm text-gray-500">{description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {templateType === 'ecommerce' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h4 className="font-medium text-blue-900 mb-4 flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Ecommerce Configuration
          </h4>

          <div className="space-y-6">
            {/* Cart Badge Style */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cart Badge Display
              </label>
              <select
                value={formData.headerSettings?.cartBadgeStyle || 'count'}
                onChange={(e) => handleToggleChange('cartBadgeStyle', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="count">Show Item Count</option>
                <option value="total">Show Total Amount</option>
                <option value="both">Show Count & Total</option>
                <option value="dot">Show Indicator Dot Only</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                How to display the cart badge when items are added
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cart Icon Type
              </label>
              <select
                value={formData.headerSettings?.cartIconType || 'auto'}
                onChange={(e) => handleToggleChange('cartIconType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="auto">Auto (based on store type)</option>
                <option value="cart">Shopping Cart Icon</option>
                <option value="calendar">Calendar/Bookings Icon</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                'Auto' shows calendar for service stores, cart for product stores
              </p>
            </div>

            {/* Category Display Limit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category Display Limit (Desktop)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min="3"
                  max="12"
                  value={formData.headerSettings?.categoryDisplayLimit || 6}
                  onChange={(e) => handleToggleChange('categoryDisplayLimit', parseInt(e.target.value))}
                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="w-12 text-sm text-gray-600 text-right">
                  {formData.headerSettings?.categoryDisplayLimit || 6}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Number of categories to show in header before "More" button
              </p>
            </div>

            {/* Additional Toggles */}
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.headerSettings?.stickyCartButton ?? true}
                  onChange={(e) => handleToggleChange('stickyCartButton', e.target.checked)}
                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Sticky Cart Button</div>
                  <div className="text-sm text-gray-500">
                    Keep cart button visible when scrolling down the page
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.headerSettings?.showCategoryIcons ?? false}
                  onChange={(e) => handleToggleChange('showCategoryIcons', e.target.checked)}
                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Show Category Icons</div>
                  <div className="text-sm text-gray-500">
                    Display icons next to category names (if available)
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.headerSettings?.highlightActiveCategory ?? true}
                  onChange={(e) => handleToggleChange('highlightActiveCategory', e.target.checked)}
                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Highlight Active Category</div>
                  <div className="text-sm text-gray-500">
                    Show visual indicator for currently selected category
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Header Layout & Styling */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
          <Palette className="h-4 w-4" />
          Header Styling
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Header Height */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Header Height
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="40"
                max="120"
                value={formData.headerSettings?.headerHeight || 80}
                onChange={(e) => handleHeightChange('headerHeight', e.target.value)}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="w-16 px-2 py-1 text-sm text-gray-700 text-center bg-gray-100 rounded border border-gray-300">
                {formData.headerSettings?.headerHeight || 80}px
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Adjust the overall header height (60-120px)
            </p>
          </div>


          {/* Logo Height */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logo Height
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="30"
                max="80"
                value={formData.headerSettings?.logoHeight || 40}
                onChange={(e) => handleHeightChange('logoHeight', e.target.value)}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="w-16 px-2 py-1 text-sm text-gray-700 text-center bg-gray-100 rounded border border-gray-300">
                {formData.headerSettings?.logoHeight || 40}px
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Size of the logo in the header (30-80px)
            </p>
          </div>

          {/* Header Position */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Header Position
            </label>
            <select
              value={formData.headerSettings?.position || 'sticky'}
              onChange={(e) => handleToggleChange('position', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="sticky">Sticky (recommended)</option>
              <option value="fixed">Fixed</option>
              <option value="static">Static</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              How the header behaves when scrolling
            </p>
          </div>

          {/* Header Layout */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Header Layout
            </label>
            <select
              value={formData.headerSettings?.layout || 'default'}
              onChange={(e) => handleToggleChange('layout', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="default">Logo Left, Controls Right</option>
              <option value="centered">Centered Logo, Split Controls</option>
              <option value="minimal">Minimal (Logo only)</option>
              <option value="compact">Compact (Reduced spacing)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Overall header layout style
            </p>
          </div>
        </div>
      </div>

      {/* Search Configuration */}
      {getCheckedValue('showSearch', true) && templateType !== 'ecommerce' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Search className="h-4 w-4" />
            Search Configuration
          </h4>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Position
              </label>
              <select
                value={formData.headerSettings?.searchPosition || 'center'}
                onChange={(e) => handleToggleChange('searchPosition', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="center">Center (between logo and controls)</option>
                <option value="right">Right (with controls)</option>
                <option value="left">Left (after logo)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Placeholder Text
              </label>
              <input
                type="text"
                value={formData.headerSettings?.searchPlaceholder || 'Search media...'}
                onChange={(e) => handleToggleChange('searchPlaceholder', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search media..."
              />
            </div>
          </div>
        </div>
      )}

      {/* User Profile Settings */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
          <User className="h-4 w-4" />
          User Profile Display
        </h4>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profile Visibility
            </label>
            <select
              value={formData.headerSettings?.userProfileVisibility || 'auto'}
              onChange={(e) => handleToggleChange('userProfileVisibility', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="auto">Auto (based on access level)</option>
              <option value="always">Always Show</option>
              <option value="authenticated">Authenticated Users Only</option>
              <option value="never">Never Show</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Control when user profile/login buttons are displayed
            </p>
          </div>

          <div className="space-y-3">
            {[
              {
                key: 'showProfileForPublic',
                label: 'Show Profile on Public Albums',
                description: 'Display user profile even when album is public'
              },
              {
                key: 'requireLoginToView',
                label: 'Require Login to View',
                description: 'Force users to sign in before accessing album'
              }
            ].map(({ key, label, description }) => (
              <label key={key} className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={formData.headerSettings?.[key] ?? false}
                  onChange={(e) => handleToggleChange(key, e.target.checked)}
                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <div className="font-medium text-gray-700">{label}</div>
                  <div className="text-sm text-gray-500">{description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>


      {/* Header Preview */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-gray-900 flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Header Preview
          </h4>
          <button
            onClick={() => setHeaderPreview(!headerPreview)}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-2 font-medium"
          >
            {headerPreview ? (
              <>
                <EyeOff className="h-4 w-4" />
                Hide Preview
              </>
            ) : (
              <>
                <Eye className="h-4 w-4" />
                Show Preview
              </>
            )}
          </button>
        </div>

        {headerPreview && (
          <div className="border-2 border-gray-300 rounded-lg overflow-hidden shadow-sm">
            <HeaderPreview 
              settings={formData.headerSettings || {}}
              bookData={formData}
              styling={formData.styling || {}}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Header Preview Component
const HeaderPreview = ({ settings, bookData, styling }) => {
  const getSettingValue = (key, defaultValue = true) => {
    return settings[key] !== undefined ? settings[key] : defaultValue;
  };

  return (
    <div 
      className="bg-white border-b border-gray-200"
      style={{ 
        backgroundColor: styling.headerColor || '#ffffff',
        height: `${settings.headerHeight || 80}px`
      }}
    >
      <div className="max-w-full mx-auto px-4 h-full">
        <div className="flex items-center justify-between h-full gap-4">
          {/* Left Section - Logo & Title */}
          <div className="flex items-center gap-3">
            {getSettingValue('showOwnerControls', true) && (
              <div className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ArrowLeft className="h-4 w-4 text-gray-600" />
              </div>
            )}
            
            {getSettingValue('showLogo', true) && (
              <div 
                className="bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ 
                  width: `${settings.logoHeight || 40}px`,
                  height: `${settings.logoHeight || 40}px`
                }}
              >
                <Layout className="h-4 w-4 text-gray-500" />
              </div>
            )}

            {getSettingValue('showTitle', true) && (
              <div className="min-w-0">
                <div 
                  className="font-bold text-gray-900 truncate"
                  style={{ color: styling.primaryColor || styling.headerTextColor }}
                >
                  {bookData.title || bookData.botName || 'Album Title'}
                </div>
                {getSettingValue('showDescription', false) && bookData.description && (
                  <div className="text-sm text-gray-600 truncate">
                    {bookData.description || 'Album description'}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Center Section - Search */}
          {getSettingValue('showSearch', true) && settings.searchPosition === 'center' && (
            <div className="flex-1 max-w-md hidden md:block">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={settings.searchPlaceholder || 'Search media...'}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled
                />
              </div>
            </div>
          )}

          {/* Right Section - Actions */}
          <div className="flex items-center gap-2">
            {getSettingValue('showAccessLevel', true) && (
              <div className="hidden md:flex items-center gap-2 text-xs text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full">
                <Globe className="h-3 w-3" />
                <span>Public</span>
              </div>
            )}

            {getSettingValue('showShareButton', true) && (
              <button className="hidden md:flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                <Share2 className="h-4 w-4" />
                <span>Share</span>
              </button>
            )}

            {getSettingValue('showFilterControls', true) && (
              <button className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors">
                <Filter className="h-4 w-4" />
                <span className="hidden sm:inline">Filters</span>
              </button>
            )}

            <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors md:hidden">
              <User className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Controls Section Preview */}
        {getSettingValue('showFilterControls', true) && (
          <div className="flex items-center gap-3 py-3 border-t border-gray-100 text-sm">
            {getSettingValue('showGallerySelector', true) && (
              <select className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white" disabled>
                <option>All Galleries</option>
              </select>
            )}

            {getSettingValue('showViewModeToggle', true) && (
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button className="px-3 py-1.5 bg-blue-50 text-blue-600 text-sm font-medium">
                  <Grid className="h-4 w-4" />
                </button>
                <button className="px-3 py-1.5 hover:bg-gray-50 text-sm">
                  <List className="h-4 w-4" />
                </button>
              </div>
            )}

            <div className="flex-1" />
            
            <div className="text-xs text-gray-500">
              Preview Mode
            </div>
          </div>
        )}
      </div>
    </div>
  );
};